import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { View, StyleSheet } from 'react-native';

export interface LiveCameraViewRef {
  capturePhoto: () => Promise<string>;
}

interface Props {
  style?: object;
}

const LiveCameraView = forwardRef<LiveCameraViewRef, Props>((props, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then(stream => {
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(err => console.error('LiveCameraView: getUserMedia error', err));

    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  useImperativeHandle(ref, () => ({
    capturePhoto: () =>
      new Promise<string>((resolve, reject) => {
        const video = videoRef.current;
        if (!video) { reject(new Error('No video element')); return; }
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')!.drawImage(video, 0, 0);
        canvas.toBlob(
          blob => {
            if (!blob) { reject(new Error('Canvas capture failed')); return; }
            resolve(URL.createObjectURL(blob));
          },
          'image/jpeg',
          0.8,
        );
      }),
  }));

  return (
    <View style={[styles.container, props.style]}>
      {/* @ts-ignore — video is a valid web DOM element */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </View>
  );
});

export default LiveCameraView;

const styles = StyleSheet.create({
  container: { overflow: 'hidden' },
});
